'use client';

import { useEffect, useId, type KeyboardEvent } from 'react';

import {
  PRODUCT_TABS_ANCHOR,
  showProductTab,
  useProductTabFromUrl,
  type ProductTab,
} from '@eliadmin/core/src/components/dlz/product-detail/anchors';
import { useDetailContent } from '@eliadmin/core/src/components/dlz/product-detail/details/product-detail-content';
import { ProductDetails } from '@eliadmin/core/src/components/dlz/product-detail/details/product-details';
import { ProductDownloads } from '@eliadmin/core/src/components/dlz/product-detail/details/product-downloads';
import {
  ProductReviews,
  ProductReviewsPreview,
} from '@eliadmin/core/src/components/dlz/product-detail/reviews/product-reviews';
import { TabBar, TabButton } from '@eliadmin/core/src/components/dlz/ui/tabs';
import { Container } from '@eliadmin/core/src/components/ui/container';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { useVariantsStore } from '@eliadmin/core/src/stores/variant-store';

const KEY_TARGET: Record<string, (index: number, count: number) => number> = {
  ArrowRight: (index, count) => (index + 1) % count,
  ArrowLeft: (index, count) => (index - 1 + count) % count,
  Home: () => 0,
  End: (_, count) => count - 1,
};

/** Labels of the design that the dictionary has no key for („Hodnotenie“: text447 is „Recenzie“). */
const LABEL = { details: 'Popis a parametre', reviews: 'Hodnotenie', bar: 'Informácie o produkte' };

const tabIdOf = (baseId: string, key: ProductTab) => `${baseId}-tab-${key}`;
const panelIdOf = (baseId: string, key: ProductTab) => `${baseId}-panel-${key}`;

/**
 * Figma „Tabs“ (60137:8036): 52 px tabs (47 px on mobile) over a 2 px border/subtle line, the
 * selected one underlined in brand red. The line is the bar background, so it also runs under the
 * space after the last tab and stays put while the tabs scroll on small screens. On phones the bar
 * runs to the right edge of the screen (Figma 60459:102804); its end padding keeps the last tab
 * on the container edge when scrolled to the end.
 */
const BAR = '-mr-4 border-b-0 pr-4 shadow-[inset_0_-2px_0_#E2E4E7] md:mr-0 md:pr-0';
const TAB = 'h-[47px] lg:h-[52px] lg:pt-[13px]';
const TAB_IDLE = 'text-[#6B7079]';

/**
 * Tabs under the product: „Popis a parametre“ (description, parameters, text fields, the reviews
 * teaser band), „Hodnotenie (N)“ and „Na stiahnutie“. The URL hash selects the tab (the first
 * available one without it); only the selected panel is rendered – the description is therefore in
 * the server HTML, as in the core tabs.
 */
export function ProductDetailsTabs() {
  const { t } = useTranslations();
  const baseId = useId();
  const product = useVariantsStore(s => s.product);
  const settings = useGlobalStore(s => s.templateSettings.product_detail);
  const details = useDetailContent();
  const urlTab = useProductTabFromUrl();

  const reviewCount = product.reviews_count ?? product.ratingCount;
  const tabs: { key: ProductTab; label: string }[] = [
    ...(details.description || details.parameters.length || details.textFields.length
      ? [{ key: 'popis' as const, label: LABEL.details }]
      : []),
    ...(settings?.reviews?.enabled
      ? [
          {
            key: 'hodnotenia' as const,
            label: reviewCount ? `${LABEL.reviews} (${reviewCount})` : LABEL.reviews,
          },
        ]
      : []),
    ...(product.attachments?.length || (settings?.video?.enabled && product.video)
      ? [{ key: 'na-stiahnutie' as const, label: t('text828') }]
      : []),
  ];
  const active = tabs.find(tab => tab.key === urlTab) ?? tabs[0];

  // On small screens the bar scrolls: a tab selected from elsewhere (the URL, the buy box links) is
  // scrolled just into view, a tab that is already visible leaves the bar where it is.
  const activeKey = active?.key;
  useEffect(() => {
    const tab = activeKey ? document.getElementById(tabIdOf(baseId, activeKey)) : null;
    const bar = tab?.parentElement;
    if (!tab || !bar || bar.scrollWidth <= bar.clientWidth) return;

    const barBox = bar.getBoundingClientRect();
    const tabBox = tab.getBoundingClientRect();
    const end = barBox.right - parseFloat(getComputedStyle(bar).paddingRight);
    const offset =
      tabBox.left < barBox.left ? tabBox.left - barBox.left : Math.max(0, tabBox.right - end);
    if (offset) bar.scrollBy({ left: offset });
  }, [activeKey, baseId]);

  if (!active) return null;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const target = KEY_TARGET[event.key];
    if (!target) return;

    event.preventDefault();
    const next = tabs[target(tabs.indexOf(active), tabs.length)];
    showProductTab(next.key, { scroll: false });
    document.getElementById(tabIdOf(baseId, next.key))?.focus();
  };

  return (
    <>
      <section data-section="product-tabs" className="pb-12 pt-6 lg:pb-20 lg:pt-0">
        <Container className="flex flex-col gap-6 lg:gap-10">
          {/* Key events bubble up from the focused tab. */}
          <div
            id={PRODUCT_TABS_ANCHOR}
            onKeyDown={onKeyDown}
            className="scroll-mt-20 lg:scroll-mt-28"
          >
            <TabBar label={LABEL.bar} className={BAR}>
              {tabs.map(tab => (
                <TabButton
                  key={tab.key}
                  id={tabIdOf(baseId, tab.key)}
                  controls={panelIdOf(baseId, tab.key)}
                  selected={tab === active}
                  onSelect={() => showProductTab(tab.key, { scroll: false })}
                  className={tab === active ? TAB : `${TAB} ${TAB_IDLE}`}
                >
                  {tab.label}
                </TabButton>
              ))}
            </TabBar>
          </div>
          <div
            role="tabpanel"
            id={panelIdOf(baseId, active.key)}
            aria-labelledby={tabIdOf(baseId, active.key)}
          >
            {active.key === 'popis' ? <ProductDetails /> : null}
            {active.key === 'hodnotenia' ? <ProductReviews /> : null}
            {active.key === 'na-stiahnutie' ? <ProductDownloads /> : null}
          </div>
        </Container>
      </section>
      {active.key === 'popis' ? (
        <div className="hidden border-y border-[#E2E4E7] has-[[data-section=product-reviews-preview]]:block">
          <Container className="py-12 lg:py-20">
            <ProductReviewsPreview />
          </Container>
        </div>
      ) : null}
    </>
  );
}
